import { createAsyncThunk } from '@reduxjs/toolkit';
import { managerAuth } from '../../../api/managerAuthApi';
import { type IManagerUser } from '../../../types/IManagerAuth';
import {
  CANCEL_CODE,
  getErrorData,
  type IErrorData
} from '../../../utils/getErrorData';
import {
  parseRateLimitSeconds,
  isRateLimitError
} from '../../../utils/parseRateLimitError';
import {
  startRateLimit,
  decrementRateLimit,
  setRateLimitTimer
} from '../index';

let emailAbortController: AbortController | null = null;
let codeAbortController: AbortController | null = null;

/**
 * Запускает таймер обратного отсчета для rate limit
 */
export const startRateLimitTimer = createAsyncThunk<
  undefined,
  { seconds: number; email: string },
  { rejectValue: IErrorData }
>('auth/startRateLimitTimer', async ({ seconds, email }, { dispatch }) => {
  dispatch(startRateLimit({ seconds, email }));

  const timerId = setInterval(() => {
    dispatch(decrementRateLimit());
  }, 1000);

  dispatch(setRateLimitTimer(timerId));
  return undefined;
});

/**
 * Thunk для отправки email (первый шаг авторизации)
 */
export const sendEmailThunk = createAsyncThunk<
  { message: string },
  string,
  { rejectValue: IErrorData | undefined }
>('auth/sendEmail', async (email, { rejectWithValue, dispatch }) => {
  try {
    emailAbortController?.abort();

    emailAbortController = new AbortController();

    const result = await managerAuth<{ message: string }>(
      email,
      undefined,
      emailAbortController
    );

    if (result === undefined) {
      return rejectWithValue({ message: 'Не удалось отправить код' });
    }

    if (result.meta.error !== null) {
      const errorMessage =
        typeof result.meta.error === 'string'
          ? result.meta.error
          : result.meta.error.message;
      return rejectWithValue({ message: errorMessage });
    }

    if (result.response === null) {
      return rejectWithValue({ message: 'Некорректный ответ сервера' });
    }

    return result.response;
  } catch (error: unknown) {
    const extractedError = getErrorData(error);

    if (extractedError.code === CANCEL_CODE) {
      return rejectWithValue(undefined);
    }

    // Проверяем, является ли это ошибкой rate limit
    if (isRateLimitError(extractedError.message)) {
      const seconds = parseRateLimitSeconds(extractedError.message);
      if (seconds !== null && seconds > 0) {
        // Запускаем таймер обратного отсчета
        dispatch(startRateLimitTimer({ seconds, email }));
      }
    }

    return rejectWithValue(extractedError);
  } finally {
    emailAbortController = null;
  }
});

/**
 * Thunk для подтверждения кода (второй шаг авторизации)
 */
export const confirmCodeThunk = createAsyncThunk<
  IManagerUser,
  { email: string; code: string },
  { rejectValue: IErrorData | undefined }
>('auth/confirmCode', async ({ email, code }, { rejectWithValue }) => {
  try {
    codeAbortController?.abort();

    codeAbortController = new AbortController();

    const result = await managerAuth<IManagerUser>(
      email,
      code,
      codeAbortController
    );

    if (result === undefined) {
      return rejectWithValue({ message: 'Не удалось подтвердить код' });
    }

    if (result.meta.error !== null) {
      const errorMessage =
        typeof result.meta.error === 'string'
          ? result.meta.error
          : result.meta.error.message;
      return rejectWithValue({ message: errorMessage });
    }

    if (result.response === null) {
      return rejectWithValue({ message: 'Некорректный ответ сервера' });
    }

    return result.response;
  } catch (error) {
    const extractedError = getErrorData(error);

    if (extractedError.code === CANCEL_CODE) {
      return rejectWithValue(undefined);
    }

    return rejectWithValue(extractedError);
  } finally {
    codeAbortController = null;
  }
});

/**
 * Отмена запроса отправки email
 */
export const cancelEmailRequest = (): void => {
  if (emailAbortController !== null) {
    emailAbortController.abort();
    emailAbortController = null;
  }
};

/**
 * Отмена запроса подтверждения кода
 */
export const cancelCodeRequest = (): void => {
  if (codeAbortController !== null) {
    codeAbortController.abort();
    codeAbortController = null;
  }
};
